import React, { useEffect, useState } from "react";
import Layout from "src/components/app/layout/Layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "src/components/app/layout/Header";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { orderSummaryRequest } from "src/redux/slices/labTestSlice";
import Loader from "src/components/common/Loader";
import { OrderItem, OrderSummary } from "src/types/labTests.types";
import BillingView from "src/components/app/lab-tests/BillingView";
import Card from "src/components/app/Card/Card";
import {
  Mail,
  HomeIcon,
  Building2,
  Phone,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import moment from "moment";
import Button from "src/components/app/Button";
import Badge from "src/components/common/Badge";
import * as walletActions from "src/redux/slices/walletSlice";

export const TestItemSummary: React.FC<{
  summaryData: OrderSummary | null;
  index: number;
  appointmentOn?: Date | string | null;
  timeSlot?: string | null;
}> = ({ summaryData, index, appointmentOn, timeSlot }) => {
  const patientContactNo = useAppSelector(
    (state) => state.auth.patient.data?.contact_no
  );
  const patientCountryCode = useAppSelector(
    (state) => state.auth.patient.data?.country_code
  );
  const [showDetail, setShowDetail] = useState<boolean>(true);

  const getCurrentPosition = () => {
    const lastIndex = summaryData?.order_status_data?.reduceRight(
      (acc, item, index) => {
        if (acc === -1 && item.done === "Yes") {
          return index;
        }
        return acc;
      },
      -1
    );
    return lastIndex;
  };

  const currentStep = getCurrentPosition();
  return (
    <Card
      title={summaryData?.member?.name}
      key={index}
      stackTitleAndDescription={true}
    >
      <div className="flex items-start mt-4">
        {summaryData?.address?.address_type === "Home" ? (
          <HomeIcon className="text-gray-600 mt-1 w-4 h-4" />
        ) : (
          <Building2 className="text-gray-600 mt-1 w-4 h-4" />
        )}
        <p className="flex-1 text-sm ml-3 text-gray-800">
          {summaryData?.address?.address}
        </p>
      </div>

      <div className="flex items-start mt-3">
        <Mail className="text-gray-600 mt-1 w-4 h-4" />
        <p className="flex-1 text-sm ml-3 text-gray-800">
          {summaryData?.member?.email}
        </p>
      </div>

      <div className="flex items-start mt-3">
        <Phone className="text-gray-600 mt-1 w-4 h-4" />
        <p className="flex-1 text-sm ml-3 text-gray-800">
          {`${patientCountryCode} ${patientContactNo}`}
        </p>
      </div>

      <div>
        <hr className="my-4 border-gray-300" />
        <div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="col-span-1 text-gray-600 font-medium text-sm">
              Order ID:{" "}
            </div>
            <div className="col-span-1 text-sm text-right font-semibold">
              {summaryData?.order_id}
            </div>
          </div>
          <div className="mt-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-1 text-gray-600 font-medium text-sm">
                Appointment On:{" "}
              </div>
              <div className="col-span-1 text-sm text-right font-semibold">
                {moment(appointmentOn || summaryData?.appointment_date).format(
                  "ddd, DD MMM, yyyy"
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <div className="col-span-1 text-gray-600 font-medium text-sm">
                Time:{" "}
              </div>
              <div className="col-span-1 text-sm text-right font-semibold">
                {timeSlot ||
                  (summaryData?.slot_time.includes("-")
                    ? `${moment(
                        timeSlot || summaryData?.slot_time.split("-")[0],
                        "HH:mm"
                      ).format("HH:mm")} - ${moment(
                        summaryData?.slot_time.split("-")[1],
                        "HH:mm"
                      ).format("HH:mm")}`
                    : summaryData?.slot_time)}
              </div>
            </div>
          </div>
        </div>
        <hr className="my-4 border-gray-300" />

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-gray-700 font-medium text-sm">Status</span>
            <Badge type={"success"}>{summaryData?.order_status}</Badge>
          </div>

          <button
            className="flex items-center text-sm text-[var(--app-primary)] font-semibold"
            onClick={() => {
              setShowDetail(!showDetail);
            }}
            //className="flex items-center text-sm text-green-600 font-semibold"
          >
            {showDetail ? "Hide" : "View"}
            {showDetail ? (
              <ChevronUp className="ml-1" />
            ) : (
              <ChevronDown className="ml-1" />
            )}
          </button>
        </div>

        {showDetail && summaryData?.order_status_data && (
          <div className="mt-4 space-y-3">
            {summaryData?.order_status_data.map((step, index) => (
              <div key={index} className="flex items-start">
                <div
                  className={`w-3 h-3 rounded-full mt-1 ${
                    index <= (currentStep || 0) ? "bg-green-600" : "bg-gray-300"
                  }`}
                ></div>
                <p
                  className={`ml-3 text-sm ${
                    index <= (currentStep || 0)
                      ? "text-green-600"
                      : "text-gray-500"
                  }`}
                >
                  {step.status}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

const renderOrderItemSummary = (item: OrderItem, index: number) => {
  const rupee = "\u20B9";
  return (
    <Card key={index} stackTitleAndDescription={true}>
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-1">
          {item.imageLocation && (
            <img
              src={item.imageLocation}
              alt={item.data?.name || ""}
              className="h-9 w-9 rounded-full object-contain bg-gray-100"
            />
          )}
          <p className="text-base font-semibold text-gray-800 ml-3 flex-1">
            {item?.data?.name}
          </p>
        </div>
        <div className="flex items-center">
          <span className="text-sm text-gray-600 mr-2">
            {item?.type === "bcp" ? "Free" : `${rupee}${item?.data?.price}`}
          </span>
        </div>
      </div>
    </Card>
  );
};

const OrderDetailsScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const bookTestState = useAppSelector((state) => state.labTest.bookTest.data);
  const orderSummaryState = useAppSelector(
    (state) => state.labTest.orderSummary
  );
  const currentUsedWalletMoney = useAppSelector(
    (state) => state.wallet.currentUsedWalletMoney
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const payload = {
      // order_master_id: bookTestState?.order_master_id, // 21ca56b3-8461-11f0-8966-0022486e34ba
      order_master_id:
        location?.state?.order_master_id || bookTestState?.order_master_id,
    };
    new Promise((resolve, reject) => {
      dispatch(orderSummaryRequest({ resolve, reject, payload }));
    });
  }, []);

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div className="">
        <Header
          title="Order Details"
          titlePosition="center"
          showBackButton={false}
          // onBackClick={() => {
          //   //navigate(-1);
          // }}
        />
        <div className="flex-grow p-4 overflow-y-auto h-[calc(100vh-140px)]">
          {orderSummaryState?.isLoading ? (
            <Loader />
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <TestItemSummary
                  summaryData={orderSummaryState?.data}
                  index={0}
                />
              </div>
              <div>
                {orderSummaryState?.data?.items.map(renderOrderItemSummary)}
              </div>
              <Card stackTitleAndDescription={true}>
                <BillingView
                  billingTitle="Item Total"
                  discountTitle="Discount on Item(s)"
                  orderTotal={
                    orderSummaryState?.data?.order_total?.toString() || "0"
                  }
                  homeCollectionCharge={
                    orderSummaryState?.data?.home_collection_charge || 0
                  }
                  serviceCharge={orderSummaryState?.data?.service_charge || 0}
                  finalPayableAmount={
                    orderSummaryState?.data?.final_payable_amount || 0
                  }
                  discount={
                    Number(orderSummaryState?.data?.order_total) -
                    Number(orderSummaryState?.data?.payable_amount)
                  }
                  couponDiscount={Number(
                    orderSummaryState?.data?.coupon_discount || 0
                  )}
                  isFromOrderSummary={true}
                  usedWalletMoney={currentUsedWalletMoney || 0}
                />
              </Card>
            </div>
          )}
        </div>
      </div>
      <div className="fixed bottom-0 flex items-center justify-center left-0 right-0 bg-white p-4 border-t border-gray-200">
        <Button
          rounded="full"
          fullWidth
          onClick={() => {
            navigate(`/app/${supportProgramKey}/book-lab-test/order-history`);
            dispatch(walletActions.setCurrentUsedWalletMoney(0));
          }}
        >
          Continue
        </Button>
      </div>
    </Layout>
  );
};

export default OrderDetailsScreen;
